import React, { useState, useEffect } from 'react';
import { Input, Button, Modal, Tag, Checkbox, AutoComplete, Alert, Form, Row, Col, message } from 'antd';
import _ from 'lodash';
import constants from '../../constants';
import session from '../../utils/session';
import config from '../../config';
import resources from '../../reducers/templates/resources';
import mixpanel from '../../utils/mixpanel';

const defaultTemplateIds = [
    '0e23c61f-f49b-40c9-bf8f-7f08d9406b24',
    '2f3c8d23-50bb-4dc3-8cc6-843c9dd4a74a',
    'a8d792a9-04c4-472d-8083-80053a4a934c'
];

// Canonical merge tokens. The backend resolver also accepts the historical
// synonyms ({{recruitername}}, {{brandname}}, {{company}}) for back-compat with
// templates authored before the synonym table landed.
const mailMergeFields = [
    { name: 'First Name', value: '{{firstname}}' },
    { name: 'Last Name', value: '{{lastname}}' },
    { name: 'Recruiter Name', value: '{{recruiter_name}}' },
    { name: 'Brand Name', value: '{{brand_name}}' },
    // Populated by the Chrome extension's Workday scrape (contactdetail row);
    // empty for contacts without one.
    { name: 'Workday Position', value: '{{workday_position}}' },
];

const CreateTemplateModal = ({
    open,
    onClose,
    onSuccess,
    editTemplate,
    existingCategories,
}) => {
    const isEdit = !!editTemplate?.id;
    const isDefaultTemplate = isEdit && defaultTemplateIds.includes(editTemplate.id);

    const userData = session.get('userData');
    const userRole = userData.role;
    const isOwner = !isEdit || editTemplate.user?.id === userData.id ||
        userRole === config.userRoles.ghAdmin || userRole === config.userRoles.companyAdmin;

    const [title, setTitle] = useState('');
    const [details, setDetails] = useState('');
    const [category, setCategory] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Populate fields when editing
    useEffect(() => {
        if (editTemplate) {
            setTitle(editTemplate.name || '');
            setDetails(editTemplate.details || editTemplate.message || '');
            setCategory(editTemplate.category || '');
            setIsPrivate(editTemplate.privacy === 'private');
        } else {
            setTitle('');
            setDetails('');
            setCategory('');
            setIsPrivate(false);
        }
        setErrors({});
        setCursorPosition(0);
    }, [editTemplate, open]);

    const validate = () => {
        const newErrors = {};
        if (!title || !title.trim()) newErrors.title = 'This is required';
        if (!details || !details.trim()) newErrors.details = 'This is required';

        // Check mail merge variables for special characters
        const regExp = /{{([\s\S]+?)}}/g;
        const pattern = /[~`!#$%\^&*+=\-\[\]\\';,/{}|\\":<>\? ]/g;
        const match = details.match(regExp);
        if (match && match.length) {
            match.forEach(item => {
                const temp = item.slice(2, item.length - 2);
                if (temp.match(pattern)) {
                    newErrors.details = 'No spaces or special characters allowed in variables';
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const body = {
            name: title,
            details: _.trim(details),
            category: category || 'Uncategorized',
            privacy: isPrivate ? 'private' : 'public'
        };

        setSubmitting(true);
        try {
            if (isEdit) {
                mixpanel.track('template:update template');
                await resources.updateTemplate(editTemplate.id, body);
                message.success('Template updated');
            } else {
                mixpanel.track('template:create template');
                await resources.createTemplate(body);
                message.success('New template created');
            }
            onSuccess();
            onClose();
        } catch (err) {
            message.error(err.message || `Error ${isEdit ? 'updating' : 'creating'} template`);
        } finally {
            setSubmitting(false);
        }
    };

    const addMailMerge = (value) => {
        const before = details.substring(0, cursorPosition);
        const after = details.substring(cursorPosition);
        const body = before + value + after;
        if (body.length > constants.maxMessageLength) {
            message.warning(`Template body must not exceed ${constants.maxMessageLength} characters`);
        } else {
            setDetails(body);
            setCursorPosition(cursorPosition + value.length);
        }
    };

    const handleCursorChange = (e) => {
        if (e.target.selectionStart !== undefined) {
            setCursorPosition(e.target.selectionStart);
        }
    };

    const categoryOptions = _.uniqBy(
        (existingCategories || []).map(cat => ({ value: cat })),
        'value'
    );

    const canEdit = !isDefaultTemplate && isOwner;

    const footer = [];
    if (isDefaultTemplate) {
        footer.push(
            <Button key="close" onClick={onClose}>Close</Button>
        );
    } else {
        footer.push(
            <Button key="cancel" onClick={onClose} disabled={submitting} style={{ marginRight: 8 }}>Cancel</Button>,
            <Button
                key="submit"
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
            >
                {isEdit ? 'Submit' : 'Create'}
            </Button>
        );
    }

    return (
        <Modal
            title={<span style={{ fontWeight: 600, fontSize: 18 }}>{isEdit ? 'Edit Template' : 'Create New Template'}</span>}
            open={open}
            onCancel={onClose}
            footer={footer}
            width={720}
            maskClosable={!submitting}
        >
            {isEdit && editTemplate?.user && editTemplate.user.id !== userData.id && (
                <Alert
                    type="info"
                    showIcon
                    message={`This template belongs to ${editTemplate.user.name}`}
                    style={{ marginBottom: 20 }}
                />
            )}

            <Form layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Template Title"
                            validateStatus={errors.title ? 'error' : ''}
                            help={errors.title}
                        >
                            <Input
                                value={title}
                                onChange={e => { setTitle(e.target.value); setErrors(prev => ({ ...prev, title: null })); }}
                                placeholder="e.g. Follow up email"
                                disabled={!canEdit}
                                autoFocus
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Category">
                            <AutoComplete
                                value={category}
                                onChange={val => setCategory(val || '')}
                                options={categoryOptions}
                                placeholder="Select or type a category"
                                filterOption={(input, option) => option.value.toUpperCase().indexOf(input.toUpperCase()) !== -1}
                                style={{ width: '100%' }}
                                allowClear
                                disabled={!canEdit}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {canEdit && (
                    <Form.Item style={{ marginBottom: 16 }}>
                        <Checkbox
                            checked={isPrivate}
                            onChange={e => setIsPrivate(e.target.checked)}
                        >
                            Make this template private
                        </Checkbox>
                    </Form.Item>
                )}

                <Form.Item
                    label="Message"
                    validateStatus={errors.details ? 'error' : ''}
                    help={errors.details}
                >
                    <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 13, color: '#595959', marginRight: 4 }}>Mail Merge Fields:</span>
                        {mailMergeFields.map(mail => (
                            <Tag
                                key={mail.value}
                                style={{ cursor: canEdit ? 'pointer' : 'default' }}
                                onClick={() => canEdit && addMailMerge(mail.value)}
                            >
                                {mail.name}
                            </Tag>
                        ))}
                    </div>
                    <Input.TextArea
                        value={details}
                        onChange={e => { setDetails(e.target.value); setErrors(prev => ({ ...prev, details: null })); handleCursorChange(e); }}
                        rows={6}
                        maxLength={constants.maxMessageLength}
                        showCount
                        placeholder="Type your message here..."
                        disabled={!canEdit}
                        onClick={handleCursorChange}
                        onKeyDown={handleCursorChange}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateTemplateModal;
